import { format } from 'date-fns';
import React, { FC, memo, useContext } from 'react';
import { Helmet } from 'react-helmet-async';
import { PresenterProps } from 'src/components/OperationDirectionsComponent';
import LicenseContext from 'src/contexts/LicenseContext';
import numberDecorator from 'src/decorators/number.decorator';
import { DirectionDisplaySettingEntity } from 'src/entities/DirectionDisplaySetting.entity';
import { DisplayOperationColumnEntity } from 'src/entities/DisplayOperationColumn.entity';
import { DisplayOperationRowEntity } from 'src/entities/DisplayOperationRow.entity';
import { Place, ShiftWithCycleModel } from 'src/models/ShiftModel';
import { DisplayOperationColumnKeyVo } from 'src/vo/DisplayOperationColumnKey.vo';

import style from './print.module.css';

export type ActionKind = '積' | '降' | null;

function mergeBySameRow(
  input: DisplayOperationColumnEntity[][]
): DisplayOperationColumnEntity[][] {
  const groups = new Map<string, DisplayOperationColumnEntity[]>();

  input.forEach((row) => {
    // 一致判定するデータで識別キーを作る
    const identificationKey = row
      .filter((f) => f.key !== '数量' && f.key !== '合計重量' && f.key !== '合計体積' && f.key !== '到着予定時刻')
      .map((f) => `${f.key}:${f.value}:${f.klass}`)
      .join('|');

    // 識別キーでグループ化
    if (!groups.has(identificationKey)) {
      groups.set(
        identificationKey,
        row.map((f) => ({ ...f }))
      );
    } else {
      // 既存グループの数量・合計重量・合計体積を合算
      const existing = groups.get(identificationKey);
      if (existing) {
        row.reduce((_, thisField) => {
          const field = existing.find((f) => f.key === thisField.key);
          if (field && ['数量', '合計重量', '合計体積', '伝票数'].includes(thisField.key)) {
            field.value = String(parseFloat(field.value) + parseFloat(thisField.value));
          }
          return _;
        }, null);
      }
    }
  });

  // マップの値を配列にして返す
  return Array.from(groups.values());
}

function formatDateTime(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d} ${hh}:${mm}`;
}

const OperationDirectionPresenter: FC<PresenterProps> = memo((
  {
    shiftWithCycleModel,
    action,
    unit,
    operations,
    isYC,
    operationDiffs,
    shiftWithCycleModels,
    shiftIndex,
  }
) => {
  const displayVolume = unit !== '才';
  const displayUnit = unit !== '才' ? '重量' : '才数';
  const licenseContext = useContext(LicenseContext);
  const selectableDisplayDirectionConditionSettings = licenseContext?.selectableDisplayDirectionConditionSettings || [];
  let settings: DirectionDisplaySettingEntity[] = licenseContext?.displayDirectionConditions?.find((it) => it.name === 'default')?.settings || [];
  if (selectableDisplayDirectionConditionSettings.length > 0 && settings.length > 0) {
    const ids = selectableDisplayDirectionConditionSettings.map((it) => it.id);
    settings = settings.filter((it) => ids.includes(it.id));
  }

  const headers: string[] = settings.map((it: DirectionDisplaySettingEntity) => it.title);
  const act: ActionKind = action === '降' ? '降' : null;

  const propertyNamesThatCanBeGrouped = [
    '運転手名',
    '車両番号',
    '拠点名',
    '到着予定時刻',
    '出発予定時刻',
    '作業場所住所',
    '作業場所緯度',
    '作業場所経度',
  ];

  const propertyNamesThatCanBeSummed = [
    '合計重量',
    '合計体積',
    '数量'
  ];

  const propertyNamesThatCanBeSummedWithIndex = propertyNamesThatCanBeSummed.map((it) => {
    const idx = headers.indexOf(it);

    if (idx === -1) return null;

    return {
      idx,
      name: it
    };
  }).filter((maybe) => maybe)
    .sort((a, b) => a.idx - b.idx);

  const deletedOrders: { toShift: ShiftWithCycleModel, deletedOperations: DisplayOperationRowEntity[] }[] = [];
  const displayKyes = ['指定トラック', '作業場所', '作業場所住所', '合計重量', '合計体積', '数量', '備考'].concat(licenseContext.config?.custom_input_fields || []);
  if (isYC) {
    const operationDiff = operationDiffs[shiftWithCycleModel.driver.id];

    if (operationDiff?.deleted && operationDiff.deleted.length > 0) {
      operationDiff.deleted.forEach((it) => {
        const toShift = shiftWithCycleModels.find((r) => r.entity.id === it.to_shift_id);
        const operationIds = toShift.allPlaces('降').flatMap((place) => place.allOperations('降'))
                                    .filter((ops) => it.order_ids.includes(ops.getOrderID())).map((ops) => ops.getID());
        const deletedOperations = operations.filter((ops) => operationIds.includes(Number(ops.find((r) => r.key === 'id').value)));
        deletedOrders.push({ toShift, deletedOperations });
      });
    }
  }

  const isDNP = !!licenseContext?.config?.company_name?.match(/.*DNPロジスティクス.*/);
  // const isDNP = !!licenseContext?.config?.company_name?.match(/.*localhost.*/);
  const dnpSummary: Place[][] = [];
  if (isDNP) {
    let prevPlace: Place = null;
    let places: Place[] = [];
    let lastPlaceIndex = 0;
    shiftWithCycleModel.allPlaces(act).forEach((place, placeIndex) => {
      if (prevPlace !== null && ((prevPlace.isAction('積') && place.isAction('降')) || (prevPlace.isAction('降') && place.isAction('積')))) {
        dnpSummary[placeIndex - 1] = places;
        places = [];
      }
      places.push(place);
      prevPlace = place;
      lastPlaceIndex = placeIndex;
    });
    if (places.length > 0) {
      dnpSummary[lastPlaceIndex] = places;
    }
  }

  const shiftId = shiftWithCycleModel.entity.id;

  return (
    <>
      <Helmet>
        <title>配送指示書</title>
      </Helmet>
      <div className={style.page} style={{ page: 'A4OperationDirection' }}>
        <div className={style.line}>
          <span className={style.left}>
            <h1>
              {shiftWithCycleModel.entity.course_number != null && `コース：${shiftWithCycleModel.entity.course_number}`}
            </h1>
          </span>
          <span className={style.center}><h1>運行指示書</h1></span>
        </div>
        <div className={style.flex}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '20px' }}>
              <table style={{ height: '50%', width: 'fit-content', }}>
                <tbody>
                  <tr>
                    <th style={{ minWidth: '80px' }}>運行日</th>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>
                      {
                        format(shiftWithCycleModel.startAt(), 'yyyy-MM-dd') === format(shiftWithCycleModel.endAt(), 'yyyy-MM-dd')
                          ? format(shiftWithCycleModel.startAt(), 'yyyy/MM/dd')
                            : `${format(shiftWithCycleModel.startAt(), 'yyyy/MM/dd')} - ${format(shiftWithCycleModel.endAt(), 'yyyy/MM/dd')}`
                      }
                    </td>
                  </tr>
                  <tr>
                    <th>伝票数</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{new Set(shiftWithCycleModel.orders.map((order) => order.code)).size}</td>
                  </tr>
                </tbody>
              </table>

              <table style={{ width: 'fit-content' }}>
                <tbody>
                  <tr>
                    <th style={{ minWidth: '100px' }}>運送会社</th>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '200px' }}>{shiftWithCycleModel.entity.orders[0]?.order_assignment_attributes.carrier_company}</td>
                  </tr>
                  <tr>
                    <th>車番</th>
                    <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.licensePlateValueWithKlass()}</td>
                  </tr>
                  <tr>
                    <th>ドライバー</th>
                    <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.driverName()}</td>
                  </tr>
                  <tr>
                    <th>電話番号</th>
                    <td style={{ whiteSpace: 'nowrap' }}>{shiftWithCycleModel.driverPhoneNumber() || '-'}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <table style={{ width: 'fit-content', }}>
                <tbody>
                  <tr>
                    <th style={{ minWidth: '80px' }}>納品先数</th>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>{shiftWithCycleModel.placeCount('降')}</td>
                  </tr>
                  <tr>
                    <th>総数量</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.itemCount('降')}</td>
                  </tr>
                  <tr>
                    <th>{`総${displayUnit}`}</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{numberDecorator.round(shiftWithCycleModel.totalWeightKg('降'), 2)}</td>
                  </tr>
                  <tr>
                    <th>総体積</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{displayVolume && (numberDecorator.round(shiftWithCycleModel.totalVolume('降'), 2))}</td>
                  </tr>
                </tbody>
              </table>

              <table style={{ width: 'fit-content', }}>
                <tbody>
                  <tr>
                    <th style={{ minWidth: '80px' }}>重量積載率</th>
                    <td style={{ whiteSpace: 'nowrap', minWidth: '120px' }} className={style.textRight}>{shiftWithCycleModel.loadingWeightRateTimelineText()}</td>
                  </tr>
                  <tr>
                    <th>体積積載率</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.loadingVolumeRateTimelineText()}</td>
                  </tr>
                  <tr>
                    <th>回転数</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>{shiftWithCycleModel.cycleCountText()}</td>
                  </tr>
                  <tr>
                    <th>総走行距離</th>
                    <td style={{ whiteSpace: 'nowrap' }} className={style.textRight}>
                      {shiftWithCycleModel.totalDistanceRoundKm()}
                      km
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {shiftWithCycleModel.allPlacesGrouped(act).map((group, groupIndex) => {
          const map = operations.reduce((acc, it) => {
            const id = it.find((r) => r.key === 'id').value;
            return acc.set(Number(id), it);
          }, new Map<number, DisplayOperationRowEntity>());
          return (
            <React.Fragment key={[shiftId, groupIndex, 'fragment'].join('-')}>
              <div style={{ margin: '5mm 0mm 0mm 0mm' }} />
              {shiftWithCycleModel.operationCycles.length > 1 && (
                <div style={{ margin: '0mm 0mm 1mm 0mm' }}>
                  <strong>
                    {`<${groupIndex + 1}回転目>`}
                  </strong>
                </div>
              )}
              <table style={{ width: '100%', margin: '0mm' }} key={['OperationDirectionPresenter', 'table'].join('-')}>
                <thead style={{ border: '1px solid #000' }} key={['OperationDirectionPresenter', 'headers', 'thead'].join('-')}>
                  <tr key={['OperationDirectionPresenter', 'headers', 'tr'].join('-')}>
                    {
                      headers.map((header) => (
                        <th
                          key={
                            [
                              'OperationDirectionPresenter',
                              'headers',
                              header
                            ].join('-')
                          }
                        >
                          {header}
                        </th>
                      ))
                    }
                  </tr>
                </thead>
                {group.map((place, placeIndex) => {
                  const placeOperations = place.allOperations(act).sort((a, b) => a.compare(b));
                  const currentOperationAttributes = placeOperations.map((ops) => map.get(ops.getID()));

                  const chit = { key: '伝票数' as DisplayOperationColumnKeyVo, value: '1', klass: 'Integer' };
                  currentOperationAttributes.forEach((row) => {
                    row.push({ ...chit });
                  });

                  const displayOperationAttributes: DisplayOperationColumnEntity[][] = currentOperationAttributes
                    .map((currentOperationAttribute) => headers.map((header) => {
                      const ret = currentOperationAttribute.find((r) => r.key === header);
                      if (ret) { return ret; }

                      return {
                        key: header as DisplayOperationColumnKeyVo,
                        klass: 'NilClass',
                        value: null,
                      };
                    }));
                  const mergedOperationAttributes = mergeBySameRow(displayOperationAttributes);
                  let rowCount = 0;
                  return (
                    <React.Fragment key={[shiftId, placeIndex, place.latlng, 'fragment'].join('-')}>
                      <tbody
                        style={{ border: '1px solid #000' }}
                        key={[shiftId, placeIndex, place.latlng].join('-')}
                      >
                        {mergedOperationAttributes.map((it, index) => {
                          let rowBgColor = '#fff';
                          rowCount++;
                          if (isDNP) {
                            rowBgColor = it.find((el) => el.key === '作業')?.value === '集荷' ? 'repeating-linear-gradient(45deg, #aaa, #aaa 1px,#fff 0px, #fff 19px)' : '#fff';
                          }

                          return (
                            <tr key={[shiftId, placeIndex, place.latlng, index, 'tr'].join('-')} style={{ background: rowBgColor }}>
                              {it.map((attr) => (
                                ((index === 0 || !propertyNamesThatCanBeGrouped.includes(attr.key)) && (
                                  <td
                                    key={[shiftId, placeIndex, place.latlng, index, attr.key].join('-')}
                                    rowSpan={index === 0 && propertyNamesThatCanBeGrouped.includes(attr.key) ? place.allOperations(act).length : 1}
                                    style={{ textAlign: ['Integer', 'Float'].includes(attr.klass) ? 'right' : 'left' }}
                                  >
                                    {
                                      (() => {
                                        if (attr.key === '到着予定時刻') {
                                          return format(place.arrivalAt(), 'HH:mm');
                                        }
                                        if (['Integer', 'Float'].includes(attr.klass)) {
                                          return Number(attr.value).toLocaleString();
                                        }
                                        return attr.value;
                                      })()
                                    }
                                  </td>
                                ))
                              ))}
                            </tr>
                          );
                        })}
                      </tbody>
                      {(!isDNP && place.allOperations(act).length > 1 && rowCount > 1 && !!propertyNamesThatCanBeSummedWithIndex.length) && (
                        <tbody
                          style={{ border: '1px solid #000' }}
                          key={[shiftId, placeIndex, place.latlng, 'subtotal'].join('-')}
                        >
                          <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
                            {
                              headers.map((header, index) => {
                                const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
                                const isLast = index === headers.length - 1;
                                const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
                                  .includes(index) || isLast || sumCurrent;
                                const sumValue = () => {
                                  if (header === '合計重量') return numberDecorator.round(place.itemWeightKg(act), 2);
                                  if (header === '数量') return place.itemCount(act);
                                  if (header === '合計体積' && place.itemVolumeM3(act) > 0) return numberDecorator.round(place.itemVolumeM3(act), 2);

                                  return '';
                                };
                                return (
                                  <td
                                    key={[placeIndex, place.latlng, index, header, 'sum'].join('-')}
                                    style={{
                                      borderRightWidth: hasBorder ? '1px' : '0',
                                      textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left'
                                    }}
                                  >
                                    {
                                      (() => {
                                        if (index === 0 && sumValue() === '') {
                                          return '小計';
                                        }
                                        if (['数量', '合計重量', '合計体積'].includes(header)) {
                                          return sumValue().toLocaleString();
                                        }
                                        return sumValue();
                                      })()
                                    }
                                  </td>
                                );
                              })
                            }
                          </tr>
                        </tbody>
                      )}
                      {!isDNP && !(place.allOperations(act).length > 1 && rowCount > 1 && !!propertyNamesThatCanBeSummedWithIndex.length) && (
                        <tbody
                          style={{ border: '1px solid #000' }}
                          key={[shiftId, placeIndex, place.latlng, 'subtotal'].join('-')}
                        >
                          <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
                            <td aria-label="区切り行" colSpan={headers.length} className="text-center" key={[shiftId, placeIndex, place.latlng, 'subtotal', 'td'].join('-')}> </td>
                          </tr>
                        </tbody>
                      )}
                      {isDNP && dnpSummary[placeIndex] && (
                        <tbody
                          style={{ border: '1px solid #000' }}
                          key={[shiftId, placeIndex, place.latlng, 'subtotal'].join('-')}
                        >
                          <tr className={style.summery} key={[shiftId, placeIndex, place.latlng, 'subtotal', 'tr'].join('-')}>
                            {
                              headers.map((header, index) => {
                                const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
                                const isLast = index === headers.length - 1;
                                const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
                                  .includes(index) || isLast || sumCurrent;
                                const sumValue = () => {
                                  const sumPlaces = dnpSummary[placeIndex];
                                  if (header === '合計重量') {
                                    return numberDecorator.round(sumPlaces.map((it) => it.itemWeightKg(act)).reduce((prev, curr) => prev + curr), 2);
                                  }
                                  if (header === '数量') {
                                    return sumPlaces.map((it) => it.itemCount(act)).reduce((prev, curr) => prev + curr);
                                  }
                                  if (header === '合計体積' && sumPlaces.some((it) => it.itemVolumeM3(act) > 0)) {
                                    return numberDecorator.round(sumPlaces.map((it) => it.itemVolumeM3(act) || 0).reduce((prev, curr) => prev + curr), 2);
                                  }

                                  return '';
                                };
                                return (
                                  <td
                                    key={[placeIndex, place.latlng, index, header, 'sum'].join('-')}
                                    style={{
                                      borderRightWidth: hasBorder ? '1px' : '0',
                                      textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left'
                                    }}
                                  >
                                    {
                                      (() => {
                                        if (index === 0 && sumValue() === '') {
                                          return '小計';
                                        }
                                        if (['数量', '合計重量', '合計体積'].includes(header)) {
                                          return sumValue().toLocaleString();
                                        }
                                        return sumValue();
                                      })()
                                    }
                                  </td>
                                );
                              })
                            }
                          </tr>
                        </tbody>
                      )}
                    </React.Fragment>
                  );
                })}
                {
                  !!propertyNamesThatCanBeSummedWithIndex.length && (
                    <tbody style={{ border: '1px solid #000' }} key={[shiftId, 'total', 'tbody'].join('-')}>
                      <tr className={style.summery} key={[shiftId, 'total', 'tr'].join('-')}>
                        {
                          headers.map((header, index) => {
                            const sumCurrent = propertyNamesThatCanBeSummed.includes(header);
                            const isLast = index === headers.length - 1;
                            const hasBorder = propertyNamesThatCanBeSummedWithIndex.map((it) => it.idx - 1)
                              .includes(index) || isLast || sumCurrent;

                            const sumValue = () => {
                              const totalWeight = group.reduce((acc, it) => acc + it.itemWeightKg(action === '降' ? '降' : '積'), 0);
                              const totalItemCount = group.reduce((acc, it) => acc + it.itemCount(action === '降' ? '降' : '積'), 0);
                              const totalVolume = group.reduce((acc, it) => acc + it.itemVolumeM3(action === '降' ? '降' : '積'), 0);
                              if (header === '合計重量') return totalWeight.toLocaleString();
                              if (header === '数量') return totalItemCount.toLocaleString();
                              if (header === '合計体積' && totalVolume > 0) return totalVolume.toLocaleString();

                              return '';
                            };

                            return (
                              <td
                                key={
                                  [shiftId, 'sum', header, index].join('-')
                                }
                                style={{
                                  borderRightWidth: hasBorder ? '1px' : '0',
                                  textAlign: ['数量', '合計重量', '合計体積'].includes(header) ? 'right' : 'left',
                                }}
                              >
                                {index === 0 && sumValue() === '' ? '合計' : sumValue()}
                              </td>
                            );
                          })
                        }
                      </tr>
                    </tbody>
                  )
                }
                {isYC && (
                  deletedOrders.map((it) => (
                    <tbody style={{ borderLeft: 'none' }} key={[shiftId, it.toShift.entity.id, 'deleted', 'tbody'].join('-')}>
                      <tr style={{ borderLeft: 'none' }}>
                        <td colSpan={headers.length} style={{ borderLeft: '0px none #fff', borderRight: 'none' }}>
                          <h3>{`${shiftWithCycleModel.truck.licensePlateValue} → ${it.toShift.truck.licensePlateValue}`}</h3>
                        </td>
                      </tr>
                      {it.deletedOperations.map((ops) => (
                        <tr style={{ border: '1px solid #000' }} key={[shiftId, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, 'tr'].join('-')}>
                          {
                            headers.map((header) => {
                              const attr = ops.find((r) => r.key === header);
                              const value = displayKyes.includes(header) ? attr.value : '';
                              return (
                                <td key={[shiftId, it.toShift.entity.id, 'deleted', ops.find((r) => r.key === 'id').value, header].join('-')}>{value}</td>
                              );
                            })
                          }
                        </tr>
                      ))}
                    </tbody>
                  ))
                )}
              </table>
            </React.Fragment>
          );
        })}
        {licenseContext.config?.operation_direction_memo && (
          <div className={style.memo}>
            {
              (licenseContext.config?.operation_direction_memo || '').split('\n').map((it, index) => (
                index === 0
                ? (
                  <div key={['operation_direction_memo', index].join('-')}>
                    <strong>備考欄：</strong>
                    {it}
                  </div>
                )
                : <div key={['operation_direction_memo', index].join('-')}>{it}</div>
              ))
            }
          </div>
        )}
        <div className={style.footer}>
          {`${formatDateTime(new Date())} 発行 `}
          {`<${shiftIndex + 1}/${shiftWithCycleModels.length}>`}
        </div>
      </div>
    </>
  );
});

export default OperationDirectionPresenter;
